import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO_CONFIG = {
  home: {
    title: 'Your Free Personal Tech Roadmap and Project Builder | Tekyu',
    description: 'Get a personalized DevSecOps, Cloud Security, and SRE learning roadmap. Plan your learning, practise with projects, and prepare evidence of your skills. 100% free roadmap generation.',
    keywords: 'DevSecOps roadmap, Cloud Security roadmap, SRE roadmap, tech career planning, cybersecurity projects, free tech roadmap',
    ogTitle: 'Your Free Personal Tech Roadmap and Project Builder',
    ogDescription: 'Get a personalized learning path for DevSecOps, Cloud Security, SRE, and more. Build real projects and land your dream tech job.',
    ogImage: 'https://tekyu.io/og-image.jpg',
    twitterCard: 'summary_large_image',
  },
  roles: {
    title: 'Choose Your Tech Career Path | DevSecOps, Cloud Security, SRE & More | Tekyu',
    description: 'Select from 12+ tech career paths: DevSecOps, DevSecAI, Cloud Platform Engineer, SRE, Security Analyst, and more. Get your free personalized roadmap.',
    keywords: 'DevSecOps career, Cloud Engineer career, SRE career, Security Analyst career, tech career paths',
  },
  portal: {
    title: 'Tekyu Portal | Your Projects, Roadmaps & Progress | Tekyu',
    description: 'Access your personalized roadmaps, hands-on security projects, and track your learning progress. Build your tech career with Tekyu.',
  },
  pricing: {
    title: 'Free Tech Roadmaps and Projects | Tekyu',
    description: 'Explore free tech career roadmaps, guided projects and account-based progress tracking.',
  },
  devsecops: {
    title: 'DevSecOps Roadmap Generator | Free Personalized Learning Path | Tekyu',
    description: 'Get your free personalized DevSecOps roadmap with certifications, skills, and timeline. Build real projects and land DevSecOps jobs.',
    keywords: 'DevSecOps roadmap, DevSecOps certification, DevSecOps projects, DevSecOps jobs',
  },
  // Add more role-specific SEO configs
};

export default function SEO({ page = 'home', customTitle, customDescription }) {
  const config = SEO_CONFIG[page] || SEO_CONFIG.home;
  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const keywords = config.keywords || 'tech career, DevSecOps, Cloud Security, SRE, cybersecurity';
  const ogTitle = config.ogTitle || title;
  const ogDescription = config.ogDescription || description;
  const ogImage = config.ogImage || 'https://tekyu.io/og-image.jpg';
  const url = typeof window !== 'undefined' ? window.location.href : 'https://tekyu.io';

  // Structured Data (JSON-LD)
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Tekyu',
    description: 'Your Free Personal Tech Roadmap and Project Builder',
    url: 'https://tekyu.io',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'GBP',
    },

  };

  if (page === 'devsecops' || page.includes('roadmap')) {
    structuredData['@type'] = 'Course';
    structuredData.courseCategory = 'DevSecOps';
    structuredData.provider = {
      '@type': 'Organization',
      name: 'Tekyu',
    };
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#4ade80" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Tekyu" />

      {/* Twitter */}
      <meta name="twitter:card" content={config.twitterCard || 'summary_large_image'} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Tekyu" />
      <link rel="canonical" href={url} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Additional structured data for courses/projects */}
      {page.includes('roadmap') && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Tech Career Roadmap',
            description: 'Personalized learning path for tech careers',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Skills Assessment',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Personalized Roadmap',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Hands-on Projects',
              },
            ],
          })}
        </script>
      )}
    </Helmet>
  );
}
